/* eslint-disable react/forbid-component-props, react/no-multi-comp */
import {
  Font,
  Image,
  Link,
  Page as PagePDF,
  StyleSheet,
  Text,
  View,
} from '@react-pdf/renderer';
import {type VFC} from 'react';
import {IMAGES} from '../../../../../utils/constants/image.js';
import {
  type BaseProps,
  type ChildOnlyProp,
  type HeaderProps,
  type LinkProps,
  type ListProps,
  type LogoProps,
  type ParagraphProps,
  type SupScriptProps,
  type TabProps,
  type TextWithSupProps,
} from './types.js';

Font.register({
  family: 'LloydsBankJack',
  fonts: [
    {
      src: new URL(
        '/fonts/lloydsBankJackLight.woff',
        import.meta.url
      ).toString(),
      fontWeight: 'light',
    },
    {
      src: new URL(
        '/fonts/lloydsBankJackRegular.woff',
        import.meta.url
      ).toString(),
      fontWeight: 'normal',
    },
    {
      src: new URL(
        '/fonts/lloydsBankJackMedium.woff',
        import.meta.url
      ).toString(),
      fontWeight: 'semibold',
    },
    {
      src: new URL(
        '/fonts/lloydsBankJackBold.woff',
        import.meta.url
      ).toString(),
      fontWeight: 'bold',
    },
  ],
});

const styles = StyleSheet.create({
  banner: {
    backgroundColor: '#006A4D',
    display: 'flex',
    alignItems: 'flex-end',
    height: '50pt',
    paddingTop: '12pt',
    paddingRight: '16pt',
    marginBottom: '12pt',
  },
  quotenumber: {
    fontFamily: 'LloydsBankJack',
    color: '#024731',
    fontSize: '15pt',
    lineHeight: '1.5',
    paddingBottom: '12pt',
    borderBottom: '1pt solid #A1A1A1',
    marginBottom: '9pt',
    marginTop: '12pt',
    fontWeight: 'semibold',
  },

  lloydslogo: {
    width: '100pt',
  },
  psrlogo: {
    width: '72pt',
    marginBottom: '12pt',
  },
  servicename: {
    color: 'rgb(80, 80, 80)',
    fontFamily: 'LloydsBankJack',
    fontWeight: 'semibold',
    fontSize: '13.5pt',
    lineHeight: '1',
  },
  title: {
    color: 'rgb(2, 71, 49)',
    fontFamily: 'LloydsBankJack',
    fontWeight: 'semibold',
    fontSize: '24pt',
    marginTop: '6pt',
    marginBottom: '6pt',
  },
  header: {
    fontSize: '11pt',
    fontWeight: 'bold',
    margin: '10pt 0 5pt 20pt',
    display: 'flex',
    alignContent: 'center',
  },
  page: {
    padding: '24pt',
    fontFamily: 'LloydsBankJack',
    fontWeight: 'normal',
  },
  paragraph: {
    margin: '0 0 6pt 0',
    fontSize: '12pt',
    lineHeight: '1.2',
    fontFamily: 'LloydsBankJack',
    fontWeight: 'normal',
  },
  row: {
    paddingTop: '9pt',
    marginTop: '5pt',
    paddingBottom: '9pt',
    marginBottom: '18pt',
    borderTop: '0.5pt solid black',
    borderBottom: '0.5pt solid black',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    display: 'flex',
  },
  strong: {
    fontWeight: 'bold',
    marginLeft: '0pt',
    paddingLeft: '0pt',
  },
  table: {
    width: '100%',
    display: 'flex',
  },
  tr: {
    display: 'flex',
    flexDirection: 'row',
  },
  th: {
    fontSize: '11pt',
    fontWeight: 'bold',
    lineHeight: '1',
    padding: '3pt 4pt 4pt 4pt',
    textAlign: 'center',

    borderBottomWidth: '0.5pt',
    borderLeftWidth: '0pt',
    borderRightWidth: '0pt',
    borderTopWidth: 0,
  },
  td: {
    fontSize: '11pt',
    padding: '3pt 4pt 4pt 4pt',
    textAlign: 'center',
    borderBottomWidth: '0.5pt',
    borderLeftWidth: 0,
    borderRightWidth: 0,
    borderTopWidth: 0,
  },
  text: {
    fontSize: '12pt',
    fontFamily: 'LloydsBankJack',
    fontWeight: 'normal',
    lineHeight: '1.5',
  },
  tableWrapper: {
    border: '1pt solid #9b9b9b',
    width: '252pt',
  },
  thinBorder: {
    width: '9pt',
    height: '150pt',
    borderTopStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopWidth: '0.5pt',
    borderBottomWidth: '0.5pt',
    borderLeftWidth: '0.5pt',
    position: 'absolute',
    top: '45pt',
    left: '18pt',
  },
  sup: {
    fontSize: '7pt',
    color: '#000',
    textDecoration: 'none',
    marginTop: '1pt',
    fontWeight: 'light',
  },

  phone: {
    fontSize: '12pt',
    fontFamily: 'LloydsBankJack',
    fontWeight: 'semibold',
    lineHeight: '1.25',
    color: '#fff',
  },
  question: {
    fontSize: '12pt',
    fontFamily: 'LloydsBankJack',
    fontWeight: 'normal',
    lineHeight: '1.5',
    color: '#fff',
  },
  phoneicon: {
    display: 'flex',
    width: '36pt',
    height: '36pt',
    position: 'absolute',
    right: '18pt',
    top: '-18pt',
  },
  listItemWrapper: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: '5pt',
  },
  listItemIcon: {
    width: '12pt',
    height: '12pt',
    marginRight: '15pt',
    marginTop: '2pt',
  },
  mouseicon: {
    width: '18pt',
    height: '21pt',
    marginRight: '10pt',
  },
  clockicon: {
    width: '15pt',
    height: '15pt',
    marginRight: '5pt',
    marginLeft: '15pt',
  },
  callToAction: {
    border: '0.5pt soild #505050',
    padding: '22pt',
    marginBottom: '180pt',
  },
  callToActionHeader: {
    display: 'flex',
    flexDirection: 'row',
    borderBottom: '0.5pt soild #505050',
    marginBottom: '15pt',
  },
  applyNowCta: {
    color: '#fff',
    backgroundColor: '#006a4d',
    padding: '10pt 20pt',
    width: '180pt',
    fontSize: '12pt',
    textDecoration: 'none',
    textAlign: 'center',
    fontWeight: 'bold',
  },
});

export const PSRLogo: VFC<LogoProps> = (props) => (
  <Image style={styles.psrlogo} {...props} />
);
export const LloydsLogo: VFC<LogoProps> = (props) => (
  <Image style={styles.lloydslogo} {...props} />
);
export const MouseIcon: VFC<LogoProps> = (props) => (
  <Image style={styles.mouseicon} {...props} />
);

export const Header: VFC<HeaderProps> = ({style, ...props}) => (
  <Text style={{...styles.header, ...style}} {...props} />
);

export const Page = (props) => <PagePDF style={styles.page} {...props} />;
export const Paragraph: VFC<ParagraphProps> = ({style, ...props}) => (
  <Text style={{...styles.paragraph, ...style}} {...props} />
);
export const Sup: VFC<SupScriptProps> = ({src, ...props}) => (
  <Link src={src} style={styles.sup} {...props} />
);
export const Strong: VFC<HeaderProps> = (props) => (
  <Text style={styles.strong} {...props} />
);
export const Title: VFC<ChildOnlyProp> = (props) => (
  <Text style={styles.title} {...props} />
);
export const ServiceName: VFC<ChildOnlyProp> = (props) => (
  <Text style={styles.servicename} {...props} />
);
export const ThinBorder: VFC<ChildOnlyProp> = (props) => (
  <View style={styles.thinBorder} {...props} />
);

export const Table: VFC<TabProps> = ({style, ...props}) => (
  <View style={{...styles.table, ...style}} {...props} />
);
export const Tr: VFC<TabProps> = ({style, ...props}) => (
  <View style={{...styles.tr, ...style}} {...props} />
);
export const Th: VFC<TabProps> = ({style, ...props}) => (
  <Text style={{...styles.th, ...style}} {...props} />
);
export const Td: VFC<TabProps> = ({style, ...props}) => (
  <View style={{...styles.td, ...style}} {...props} />
);

export const TextWrapper: VFC<BaseProps> = (props) => (
  <Text style={styles.text} {...props} />
);
export const Phone: VFC<ChildOnlyProp> = (props) => (
  <Text style={styles.phone} {...props} />
);
export const Question: VFC<BaseProps> = (props) => (
  <Text style={styles.question} {...props} />
);
export const Banner: VFC<BaseProps> = (props) => (
  <View style={styles.banner} {...props} />
);
export const QuoteNumber: VFC<BaseProps> = (props) => (
  <View style={styles.quotenumber} {...props} />
);
export const PhoneIcon: VFC<LogoProps> = (props) => (
  <Image style={styles.phoneicon} {...props} />
);
export const TextWithSup: VFC<TextWithSupProps> = ({text, sup, src}) => (
  <View style={{display: 'flex', flexDirection: 'row'}}>
    <Header style={{fontWeight: '400', margin: '2pt 0 0 0pt'}}>{text}</Header>
    <Sup src={src}> {sup}</Sup>
  </View>
);

export const ListItemText: VFC<ParagraphProps> = (props) => (
  <Paragraph {...props} />
);

export const ListItemIcon: VFC<LogoProps> = (props) => (
  <Image style={styles.listItemIcon} {...props} />
);

export const ClockIcon: VFC<LogoProps> = (props) => (
  <Image style={styles.clockicon} {...props} />
);
export const ListItem: VFC<ListProps> = ({text}) => (
  <View style={styles.listItemWrapper}>
    <ListItemIcon allowDangerousPaths={true} src={IMAGES.successIconUrl} />
    <ListItemText>{text}</ListItemText>
  </View>
);

export const CallToActionWrapper: VFC<BaseProps> = (props) => (
  <View style={styles.callToAction} {...props} />
);
export const CallToActionHeader: VFC<BaseProps> = (props) => (
  <View style={styles.callToActionHeader} {...props} />
);

export const ApplyNowCTA: VFC<LinkProps> = ({src, label}) => (
  <Link src={src} style={styles.applyNowCta}>
    {label}
  </Link>
);
